import { useEffect, useState } from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { scrollToSection } from "@/components/Navbar";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  // This page runs Lenis smooth scroll, which drives an animated wrapper.
  // framer's own useScroll doesn't track reliably through it (a target-scoped
  // one pins at 0; the pixel value pins at its max), so read window.scrollY
  // directly each frame — cheap, and correct regardless of who owns scrolling.
  const scrollPx = useMotionValue(0);
  // Measured after mount, never during the first render — innerHeight can still
  // be 0 then, which would collapse every range to [0, 0] and freeze the
  // parallax at its end state.
  const [vh, setVh] = useState(0);
  const span = Math.max(vh, 400);

  useEffect(() => {
    let raf;
    let last = -1;
    const tick = () => {
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      if (y !== last) {
        last = y;
        scrollPx.set(y);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const measure = () => setVh(window.innerHeight || 0);
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
    };
  }, [scrollPx]);

  const orbY = useTransform(scrollPx, [0, span], [0, 180]);
  const fade = useTransform(scrollPx, [0, span * 0.4], [1, 0]);

  // The photo drifts down slower than the page and pushes in slightly, so the
  // hero copy appears to lift away from it as you scroll.
  // The layer is 140% tall and hung 12% above the section, leaving 12% of
  // slack at the top. Drift must stay under that or the upper edge of the
  // image is pulled into view: 8% of a 140% layer is 11.2% of the section.
  const bgY = useTransform(scrollPx, [0, span], ["0%", "8%"]);
  const bgScale = useTransform(scrollPx, [0, span], [1, 1.1]);
  const bgFade = useTransform(scrollPx, [0, span], [1, 0.3]);
  const copyY = useTransform(scrollPx, [0, span], [0, -90]);
  const copyFade = useTransform(scrollPx, [0, span * 0.75], [1, 0]);

  return (
    <section id="top" className="relative min-h-screen flex flex-col justify-center overflow-hidden" data-testid="hero-section">
      <motion.div
        style={{ y: bgY, scale: bgScale, opacity: bgFade }}
        className="absolute inset-x-0 -top-[12%] h-[140%] will-change-transform"
        data-testid="hero-parallax-bg"
      >
        {/* The source art is near-square (1402x1122) against a wide hero. The
            layer sits low enough that the MEDICAL caduceus and the division
            labels clear the fixed navbar instead of hiding behind it. */}
        <img
          src="/assets/hero-bg.jpg"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover"
          style={{ objectPosition: "center 30%" }}
        />
      </motion.div>

      {/* Scrims. The artwork is a bright blue sky, so white type needs real
          protection: darken the left where the headline sits, and let the
          imagery stay readable on the right. Bottom fade blends into Stats. */}
      <div className="absolute inset-0 bg-navy/32" />
      <div className="absolute inset-0 bg-gradient-to-r from-navy/95 via-navy/55 to-navy/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/20 to-navy/30" />
      <div className="absolute inset-0 hero-grid" />
      <motion.div
        style={{ y: orbY }}
        className="absolute -top-32 right-[-10%] w-[42rem] h-[42rem] rounded-full bg-electric/20 blur-[140px] pointer-events-none"
      />
      <motion.div
        style={{ y: orbY }}
        className="absolute bottom-0 left-[-15%] w-[36rem] h-[36rem] rounded-full bg-neon/10 blur-[140px] pointer-events-none"
      />

      <motion.div style={{ y: copyY, opacity: copyFade }} className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full pt-32 pb-20">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="font-mono-accent text-xs md:text-sm tracking-[0.35em] uppercase text-neon mb-10"
          data-testid="hero-overline"
        >
          Genoum Private Limited — Bhubaneswar · Balasore · Ghaziabad
        </motion.p>

        <h1 className="font-display uppercase leading-[0.92] tracking-tight text-[11vw] md:text-[9vw] lg:text-[8vw]" data-testid="hero-heading">
          <MaskedLine delay={0.4}>
            <span className="font-light text-slate-300">Energy.</span>
          </MaskedLine>
          <MaskedLine delay={0.55}>
            <span className="font-black text-transparent bg-clip-text bg-gradient-to-r from-electric to-neon">Engineering.</span>
          </MaskedLine>
          <MaskedLine delay={0.7}>
            <span className="font-light text-slate-300">Healthcare.</span>
          </MaskedLine>
        </h1>

        <div className="mt-14 flex flex-col md:flex-row md:items-end md:justify-between gap-10">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
            className="max-w-xl text-base md:text-lg text-slate-400 leading-relaxed"
            data-testid="hero-subcopy"
          >
            A diversified group pioneering renewable energy, solar &amp; green hydrogen,
            turnkey EPC and advanced medical technology — building a self-reliant,
            sustainable future from India.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
            className="flex items-center gap-4"
          >
            <button
              onClick={() => scrollToSection("#divisions")}
              className="group font-mono-accent text-xs tracking-[0.2em] uppercase bg-electric hover:bg-electric-hover text-white px-8 py-4 flex items-center gap-2 transition-colors duration-300"
              data-testid="hero-explore-button"
            >
              Explore Divisions
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              onClick={() => scrollToSection("#contact")}
              className="font-mono-accent text-xs tracking-[0.2em] uppercase border border-white/20 hover:border-neon hover:text-neon text-slate-300 px-8 py-4 transition-colors duration-300"
              data-testid="hero-contact-button"
            >
              Contact Us
            </button>
          </motion.div>
        </div>
      </motion.div>

      <motion.button
        style={{ opacity: fade }}
        onClick={() => scrollToSection("#stats")}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-slate-500 hover:text-neon transition-colors duration-300"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        data-testid="hero-scroll-indicator"
        aria-label="Scroll down"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}>
          <ArrowDown className="w-5 h-5" />
        </motion.div>
      </motion.button>
    </section>
  );
}
